import { ListItem, ScrollView, ThemeName, XGroup, XStack, YGroup, YStack, GroupProps } from 'tamagui'
import { useDeferredValue, useEffect, useId, useRef, useState } from 'react'
import { FormFieldContainer } from './form-field-container'
import { FormContainerBaseTypes } from './form-container-types'
import {
  CaretDownRegular,
  CheckSquareRegular,
  ListPlusRegular,
  SquareRegular,
} from '@tamagui-extras/core'
import { Platform, useWindowDimensions } from 'react-native'
import { StyledInput } from './styled-input'
import { Popover, PopoverProps } from '../popover'
import { Button } from '../button'

type Option = { label: string; value: string | number }
export type AutocompleteFieldProps = FormContainerBaseTypes & {
  options: Option[]
  multiple?: boolean
  value?: null | Option | Option[]
  onChange?: (v: null | Option | Option[]) => void
  placeholderSearch?: string
  placeholder?: string
  disableSearch?: boolean
  theme?: ThemeName
  allowNew?: boolean
  allowNewHook?: (newValue: string) => Option
  popoverProps?: PopoverProps
  wrapperProps?: GroupProps
  showHelperText?: boolean
}

type AutocompleteContext = {
  onChangeSelection: (option: Option) => void
  isSelected: (opts: Option) => boolean
}

type ConditionalOption<T extends boolean> = T extends true ? Option[] : Option

export function AutocompleteField({
  options,
  labelInline,
  labelProps,
  helperText,
  hideLabel,
  helperTextProps,
  placeholder,
  required,
  label,
  value,
  onChange,
  error,
  theme,
  allowNewHook,
  multiple = false,
  placeholderSearch,
  showHelperText,
  popoverProps,
  wrapperProps,
  isDirty,
  ...rest
}: AutocompleteFieldProps) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [opts, setOpts] = useState(options)
  const { width } = useWindowDimensions()
  const [popoverWidth, setPopoverWidth] = useState<number>(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const [selection, setSelection] = useState<ConditionalOption<typeof multiple> | null>(
    value ?? (multiple ? [] : null)
  )
  const isSelected = (item: Option) =>
    Array.isArray(selection)
      ? !!selection?.some((i) => i.value === item.value)
      : selection?.value === item.value

  const onChangeSelection = (item: Option) => {
    let newVal: ConditionalOption<typeof multiple> | null = null
    if (multiple) {
      const has = isSelected(item)
      newVal = has
        ? (selection as Option[])?.filter((i) => i.value !== item.value) ?? []
        : [...((selection as Option[]) ?? []), item]
    } else {
      newVal = isSelected(item) ? null : item
    }
    setSelection(newVal)
    if (typeof onChange === 'function') {
      onChange(newVal)
    }
  }

  useEffect(() => {
    const elWidth = inputRef.current?.offsetWidth
    if (elWidth) {
      setPopoverWidth(elWidth)
    }
  }, [width])

  const inputValue = Array.isArray(selection)
    ? selection.map((option) => option?.label).join(', ')
    : selection?.label || ''

  return (
    <FormFieldContainer
      id={id}
      error={error}
      required={required}
      labelProps={labelProps}
      label={label}
      labelInline={labelInline}
      helperText={helperText}
      helperTextProps={helperTextProps}
      hideLabel={hideLabel}
      isDirty={isDirty}
      showHelperText={showHelperText}
    >
      <XGroup size="$2" {...wrapperProps} ref={inputRef}>
        <XGroup.Item>
          <StyledInput
            flex={1}
            value={inputValue}
            theme={theme}
            textOverflow={'ellipsis'}
            placeholder={placeholder}
            focusStyle={{
              outlineWidth: 0,
            }}
            {...rest}
            borderRightWidth={0}
          />
        </XGroup.Item>
        <XGroup.Item>
          <Popover
            open={open}
            onOpenChange={setOpen}
            isBouncy
            hideArrow
            {...popoverProps}
            contentProps={{
              minWidth: popoverWidth ? popoverWidth : undefined,
              maxWidth: '100%',
              ...popoverProps?.contentProps,
            }}
            anchor={
              <Button
                icon={<CaretDownRegular />}
                onPress={() => setOpen((open) => !open)}
                plain
                borderRadius="$2"
                borderTopLeftRadius={0}
                borderBottomLeftRadius={0}
                borderLeftWidth={0}
                focusStyle={{
                  outlineWidth: 0,
                }}
                {...rest}
              />
            }
          >
            <AutocompleteInputContent
              theme={theme}
              options={opts}
              isSelected={isSelected}
              onChangeSelection={onChangeSelection}
              placeholderSearch={placeholderSearch}
              onAddNew={(newVal) => {
                if (newVal) {
                  const newItem =
                    typeof allowNewHook === 'function'
                      ? allowNewHook(newVal)
                      : {
                          value: newVal,
                          label: newVal,
                        }
                  setOpts((oldVal) => [newItem, ...oldVal])
                }
              }}
              {...rest}
            />
          </Popover>
        </XGroup.Item>
      </XGroup>
    </FormFieldContainer>
  )
}

type AutocompleteInputContentProps = AutocompleteFieldProps &
  AutocompleteContext & {
    onAddNew: (str: string) => void
  }

function AutocompleteInputContent({
  disableSearch,
  theme,
  placeholderSearch,
  options,
  allowNew,
  onAddNew,
  onChangeSelection,
  isSelected,
}: AutocompleteInputContentProps) {
  const [searchTerm, setSearchTerm] = useState<string>()
  const deferredTerm = useDeferredValue(searchTerm)
  const filteredOptions = deferredTerm?.length
    ? options.filter((i) => i.label.toLowerCase().includes(deferredTerm))
    : options
  const showSearch = !disableSearch || allowNew
  return (
    <>
      {Platform.OS === 'web' ? (
        <>
          {showSearch && (
            <XStack py={'$2'} width={'100%'}>
              <StyledInput
                theme={theme}
                placeholder={placeholderSearch}
                width={'100%'}
                onChangeText={(text) => {
                  setSearchTerm(text.toLowerCase())
                }}
                focusStyle={{
                  outlineWidth: 0,
                }}
              />
            </XStack>
          )}
          <ScrollView
            keyboardShouldPersistTaps={'always'}
            maxHeight={300}
            width={'100%'}
            marginTop={!showSearch ? '$3.5' : undefined}
            br="$3"
          >
            <AutocompleteList
              options={filteredOptions}
              onChangeSelection={onChangeSelection}
              isSelected={isSelected}
            />
            {allowNew && !filteredOptions?.length && deferredTerm && (
              <XStack justifyContent={'flex-start'} marginBottom={'$3'} marginLeft={'$3'}>
                <Button onPress={() => onAddNew(deferredTerm)} icon={<ListPlusRegular />}>
                  {deferredTerm}
                </Button>
              </XStack>
            )}
          </ScrollView>
        </>
      ) : (
        <YStack>
          {showSearch && (
            <XStack padding={'$3.5'} width={'100%'}>
              <StyledInput
                theme={theme}
                placeholder={placeholderSearch}
                width={'100%'}
                onChangeText={(text) => {
                  setSearchTerm(text.toLowerCase())
                }}
              />
            </XStack>
          )}
          <ScrollView>
            <AutocompleteList
              options={filteredOptions}
              onChangeSelection={onChangeSelection}
              isSelected={isSelected}
            />
          </ScrollView>
          {allowNew && !filteredOptions?.length && deferredTerm && (
            <XStack justifyContent={'flex-start'} marginBottom={'$3'} marginLeft={'$3'}>
              <Button onPress={() => onAddNew(deferredTerm)} chromeless icon={<ListPlusRegular />}>
                {deferredTerm}
              </Button>
            </XStack>
          )}
        </YStack>
      )}
    </>
  )
}

type AutocompleteListProps = AutocompleteContext & {
  options: AutocompleteFieldProps['options']
}

function AutocompleteList({ options, isSelected, onChangeSelection }: AutocompleteListProps) {
  return (
    <YGroup borderRadius={0}>
      {options.map((item, i) => {
        return (
          <YGroup.Item key={item.value}>
            <ListItem
              hoverTheme
              icon={isSelected(item) ? <CheckSquareRegular /> : <SquareRegular />}
              title={item.label}
              scaleIcon={1.5}
              onPress={() => onChangeSelection(item)}
            />
          </YGroup.Item>
        )
      })}
    </YGroup>
  )
}
